import {memo, useState, useEffect} from "react";
import {Edge, Handle, Position, useReactFlow} from "@xyflow/react";
import {FlowNodeData, FlowNodeTypes} from "@/types/node";
import {getSettingValueByKey} from "@/utils/index";
import NodeWithToolbar from "../components/NodeToolbar";
import useNode from "@/hooks/useNode";
import {FlowNodesData} from "./NodesData";
import {useSelectedElements} from "@/hooks/useSelectedElements";
import {cn} from "@/lib/utils";
const BranchNode = ({
  id,
  data,
  showContext,
}: {
  id: string;
  data: FlowNodeData;
  showContext: boolean;
}) => {
  const {deleteElements, getNode, getEdges} = useReactFlow();
  const {getSelectedNode} = useSelectedElements();
  const {addSiblingById} = useNode();
  const nodeConfig = FlowNodesData[data.type as keyof typeof FlowNodesData];

  const branchName = getSettingValueByKey(data.settings ?? [], "name");
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const selectedNode = getSelectedNode();
  const isNodeSelected = selectedNode?.id === id;

  useEffect(() => {
    setIsToolbarVisible(isNodeSelected);
  }, [isNodeSelected]);

  const borderColor =
    nodeConfig.styles?.borderColor || "var(--fb-border-primary)";
  const borderColorHover =
    nodeConfig.styles?.borderColorHover || "var(--fb-primary-border-light-35)";
  const borderColorActive =
    nodeConfig.styles?.borderColor || "var(--fb-primary)";
  const shadowActiveColor =
    nodeConfig.styles?.borderColorActive || "var(--fb-primary-shadow-light-12)";
  const contextColor = nodeConfig.styles?.contextColor || "transparent";

  const nodeStyles = {
    "--node-border-color": borderColor,
    "--node-border-color-hover": borderColorHover,
    "--node-border-color-active": borderColorActive,
    "--node-shadow-active-color": shadowActiveColor,
    "--node-icon-background-color": nodeConfig.styles?.iconBackgroundColor,
    ...(showContext
      ? {
          "--overlay-color":
            contextColor.length > 0 ? contextColor : "transparent",
        }
      : {}),
  };

  return (
    <div
      onMouseEnter={() => setIsToolbarVisible(true)}
      onMouseLeave={() => !isNodeSelected && setIsToolbarVisible(false)}
      className={cn(
        `w-85 flex justify-center ${showContext ? "enableContext" : ""}`,
      )}
      style={nodeStyles as React.CSSProperties}
    >
      <div
        className={cn(
          `branch-node px-4.5 py-1.5 bg-violet-lighter border border-violet rounded-full transition-all max-w-full`,
          showContext && "enableContext",
          `hover:bg-[${nodeConfig.styles?.backgroundColorActive || "var(--fb-muted)"}]`,
          isNodeSelected &&
            "shadow-[0px_0px_0px_6px_var(--node-shadow-active-color)]",
        )}
      >
        <h5
          className={`text-s font-medium overflow-hidden text-ellipsis whitespace-nowrap hasContext ${branchName ? "text-violet-dark" : "text-violet-light"}`}
        >
          {branchName?.toString() || "Branch"}
        </h5>

        <NodeWithToolbar
          nodeId={id}
          nodeType={data.type}
          isToolbarVisible={isToolbarVisible}
          onDelete={() => {
            const edges = getEdges();
            const parentNodeId = edges.find(
              (edge: Edge) => edge.target === id,
            )?.source;
            const hasSiblings =
              edges.filter((edge: Edge) => edge.source === parentNodeId)
                .length > 1;

            // Find all edges connected to the node
            const edgesToDelete = edges.filter(
              (edge: Edge) => edge.source === id || edge.target === id,
            );

            // Find placeholders connected to the node
            const placeholders = edges
              .filter((edge: Edge) => edge.source === id)
              .map((edge: Edge) => getNode(edge.target))
              .filter(
                (node: any) => node && node.type === FlowNodeTypes.PLACEHOLDER,
              );

            if (hasSiblings) {
              deleteElements({
                nodes: [{id}, ...placeholders.map((p: any) => ({id: p.id}))],
                edges: edgesToDelete.map(edge => ({id: edge.id})),
              });
            } else {
              deleteElements({
                nodes: [{id}],
                edges: edgesToDelete.map(edge => ({id: edge.id})),
              });
            }
          }}
          onMouseEnter={() => setIsToolbarVisible(true)}
          onMouseLeave={() => isNodeSelected && setIsToolbarVisible(false)}
        />

        <Handle
          type="target"
          position={Position.Top}
          isConnectable={false}
          className={"handle"}
          id={`${id}-top-target`}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          className={"handle"}
          id={`${id}-bottom-source`}
        />
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={true}
          className={"handle"}
          id={`${id}-right-source`}
        />
        <Handle
          type="source"
          position={Position.Left}
          isConnectable={true}
          className={"handle"}
          id={`${id}-left-source`}
        />
      </div>
    </div>
  );
};

export default memo(BranchNode);
